<template>
  <section data-testid="en-experiences-section">
    <!-- Theme / Story セクションは旅程セクションに連続して続けるため、推薦体験リストより前にまとめて表示する。
         slot/props 未提供のページ (例: cancellation/complete) では描画されない。 -->
    <ClientOnly>
      <div v-if="props.themeList && props.themeList.length > 0" id="themes" class="bg-beige1" data-testid="featured-theme-section">
        <slot name="theme-section" :theme-list="props.themeList" />
      </div>
      <!-- Story の下の区切り余白は次の体験セクション(index 0)の pt-20(Figma 80px) が担うため、ここでは pb を付けない(二重区切り防止)。 -->
      <div v-if="props.enStories && props.enStories.length > 0" class="bg-beige1">
        <NewStorySection :en-stories="props.enStories" :on-click-view-all="props.onClickStoryViewAll" />
      </div>
    </ClientOnly>
    <template v-for="(experienceSection, index) in props.experienceSections" :key="index + experienceSection.id">
      <div
        :class="[
          'bg-beige1',
          index === 0 ? (props.spacious ? 'pt-20' : 'pt-12 md:pt-20') : '',
          index === props.experienceSections.length - 1 ? (props.spacious ? 'pb-12' : 'pb-6 md:pb-12') : 'md:px-4',
        ]"
      >
        <div class="md:m-auto md:w-11/12 md:max-w-[1216px]">
          <section :class="props.spacious ? 'pb-20' : 'pb-12 md:pb-20'" :data-testid="`experiences-section-${index}`">
            <h3
              :id="experienceSection.id"
              :class="titleStyle({ hover: experienceSection.destinationWebPath !== '', spacious: props.spacious ?? false })"
              @mouseenter="changeTitleIcon(true)"
              @mouseleave="changeTitleIcon(false)"
            >
              <template v-if="experienceSection.destinationWebPath !== ''">
                <NuxtLink class="flex items-center" :to="experienceSection.destinationWebPath">
                  {{ experienceSection.title }}
                  <img class="h-8 w-8" :src="titleIcon" />
                  <span class="mx-2 flex flex-grow items-center">
                    <span class="h-[0.5px] flex-grow bg-beige3"></span>
                  </span>
                </NuxtLink>
              </template>
              <template v-else>
                <div class="flex items-center">
                  {{ experienceSection.title }}
                  <span class="mx-2 flex flex-grow items-center">
                    <span class="h-[0.5px] flex-grow bg-beige3"></span>
                  </span>
                </div>
              </template>
            </h3>
            <MoleculesHorizontalScrollWrapper scroll-button-box-style="h-[157px] md:h-[228px]">
              <template v-for="(experience, j) in experienceSection.experiences" :key="j + experience.id">
                <div :class="[j === 0 ? 'ml-4 md:ml-0' : '', 'shrink-0']">
                  <OrganismsPagesEnExperienceCard
                    :key="experience.id"
                    class="mb-2 w-[280px] shrink-0 overflow-hidden md:w-[360px]"
                    orientation="horizontal"
                    theme="light"
                    :aria-label="`experience card ${j}`"
                    :data-testid="`experience-card-${j}`"
                    :disable-carousel-on-sp="true"
                    :display-multiple-images="false"
                    :experience="experience"
                    :on-experience-click="(experienceSlug) => handleExperienceClick(experienceSection.webSectionId, experienceSlug)"
                  />
                </div>
              </template>
              <template v-if="experienceSection.destinationWebPath !== ''">
                <div class="max-h-[186px] min-w-[280px] max-w-full pr-[16px] md:max-h-[240px] md:min-w-[360px]">
                  <NuxtLink :to="experienceSection.destinationWebPath" @click="handleViewAll(experienceSection.webSectionId)">
                    <div class="relative flex h-full w-full flex-col items-center justify-center bg-white">
                      <img alt="view all" class="h-[80px] w-[120px]" src="~@/assets/logo/wabunka/symbol.svg" />
                      <div class="mt-2 flex items-center gap-x-1 text-light-fg-accent">
                        <span>View All</span>
                        <img class="h-3 w-3" src="~@/assets/icon/right/gold.svg" />
                      </div>
                    </div>
                  </NuxtLink>
                </div>
              </template>
            </MoleculesHorizontalScrollWrapper>
          </section>
        </div>
      </div>
      <!-- Story 直後の推薦体験セクション2個の下に Reviews セクションを表示する (slot 提供時のみ＝トップページ専用)。 -->
      <template v-if="index + 1 === 2">
        <slot name="reviews-section" />
        <!-- Reviews の直後に動画セクションを表示する (slot 提供時のみ＝トップページ専用。JCAT-5830)。 -->
        <slot name="video-section" />
      </template>
      <!-- Reviews 直後の推薦体験セクション2個の下に GTA とギフトカードのバナーセクションを表示する -->
      <div v-if="index + 1 === 4" :class="['bg-beige1', props.spacious ? 'pb-20' : 'pb-12 md:pb-20']">
        <BannerSection class="md:mx-auto md:w-11/12 md:max-w-[1216px]" />
      </div>
    </template>
  </section>
</template>

<script lang="ts" setup>
import { tv } from 'tailwind-variants';

import arrowBlackIcon from '@/assets/icon/arrow/right-black.svg';
import arrowGoldIcon from '@/assets/icon/arrow/right-gold.svg';
import type { MinimalEnStoryListViewModel } from '@/models/wabunka/enStories';
import { useIsSP } from '@/wabunka-src/common';
import BannerSection from '@/wabunka-src/common/components/pure/BannerSection/BannerSection.vue';
import type { ExperienceSectionListViewModel } from '@/wabunka-src/features/Experience';
import type { MinimumFeaturedListViewModel } from '@/wabunka-src/features/Featured';
import NewStorySection from '@/wabunka-src/features/Top/pages/components/StorySection/StorySection.vue';

interface Props {
  experienceSections: ExperienceSectionListViewModel;
  /** 新デザイン(余白 SP=80px・見出し md=30px)を使う。未指定時は従来デザイン(余白 SP=48px・見出し md=26px)。体験詳細 / cancellation は未指定で旧据え置き */
  spacious?: boolean;
  /** 指定された場合のみ推薦体験リストの前に StorySection を描画する (Top page でのみ指定) */
  enStories?: MinimalEnStoryListViewModel;
  themeList?: MinimumFeaturedListViewModel;
  /** StorySection の「View all stories」クリック時の副作用 (GA 等)。enStories とペアで指定する */
  onClickStoryViewAll?: () => void;
  onExperienceClick?: (sectionId: string, experienceSlug: string) => void;
  onViewAll?: (sectionId: string) => void;
}

const props = defineProps<Props>();

const titleStyle = tv({
  base: 'group mb-5 ml-4 scroll-mt-20 font-Spectral text-xl tracking-wide md:mb-4 md:ml-0 md:leading-[40px]',
  variants: {
    hover: { true: 'md:hover:text-light-fg-accent' },
    // 見出しサイズは spacious(Top variant)でのみ 30px。未指定(control/体験詳細/cancellation 等)は従来の 26px に据え置く
    spacious: { true: 'md:text-[30px]', false: 'md:text-[26px]' },
  },
});

const isSp = useIsSP();
const titleIcon = ref(arrowBlackIcon);

const changeTitleIcon = (hover: boolean) => {
  if (isSp.value) {
    return;
  }

  titleIcon.value = hover ? arrowGoldIcon : arrowBlackIcon;
};

const handleExperienceClick = (sectionId: string, experienceSlug: string) => {
  if (props.onExperienceClick) {
    props.onExperienceClick(sectionId, experienceSlug);
  }
};

const handleViewAll = (sectionId: string) => {
  if (props.onViewAll) {
    props.onViewAll(sectionId);
  }
};
</script>
